// rosterMoveMath.js — mirror of backend transfer_math.transfer_penalty_rate.
// Unified roster-move fee ladder (v5.7.33): 1st free, 2nd 2%, 3rd+ 5% + 2.5%
// per extra move, capped at 90% of remaining budget.
export function nextPenaltyRate(used) {
  if (used <= 0) return 0;
  if (used === 1) return 0.02;
  return Math.min(0.90, 0.05 + (used - 2) * 0.025);
}

// 2026-09-03 (owner request) — "This move will cost ₣4,920 — 30% of your
// cash" confirm before pricey roster moves. Fee = rate × remaining cash
// (matches backend: penalty is a % of budget_remaining, NOT of the price).
export const HIGH_FEE_RATE = 0.10;

// 2026-09-08 — Wildcard booster: every roster move is free while it's live
// (mirrors backend booster_rules.wildcard_active).
export function wildcardActive(team) {
  return Array.isArray(team?.active_boosters) && team.active_boosters.includes('wildcard');
}

// 2026-10-12 Activation Fix Pack — slots still open at Finalize are "free
// fills": the next add costs the player's price only (no fee, no ladder step)
// until the counter stamped at Finalize runs out (mirrors roster.py /draft).
export function freeFillsLeft(team) {
  return team?.finalized ? Math.max(0, Number(team.free_fills_remaining) || 0) : 0;
}

export function describeMoveFee(team) {
  const used = team?.weekly_transfers_used || 0;
  const wildcard = !!team?.finalized && wildcardActive(team);
  const freeFills = freeFillsLeft(team);
  const freeFill = !wildcard && freeFills > 0;
  const rate = team?.finalized && !wildcard && !freeFill ? nextPenaltyRate(used) : 0;
  const cash = team?.budget_remaining || 0;
  const fee = rate * cash;
  return {
    used, rate, cash, fee, wildcard, freeFill, freeFills,
    moveNo: used + 1,
    pct: Math.round(rate * 1000) / 10,
    free: rate <= 0,
    high: rate >= HIGH_FEE_RATE,
  };
}

export function formatFee(fee) {
  // 2-Nov 2026 (owner) — money keeps pennies everywhere: "1,234.50".
  return (Number(fee) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function freeFillLine(team) {
  const n = freeFillsLeft(team);
  if (!n) return '';
  const after = n - 1;
  return after > 0
    ? `Free fill — no roster-move fee. ${after} free fill${after === 1 ? '' : 's'} left after this one.`
    : 'Your last free fill — no roster-move fee. Further adds follow the weekly fee ladder.';
}

export function feeLine(team) {
  const f = describeMoveFee(team);
  if (f.wildcard) return 'Wildcard active — this move is free (no fee, no ladder step).';
  if (f.freeFill) return freeFillLine(team);
  if (f.free) return '';
  return `This move will cost ₣${formatFee(f.fee)} — ${f.pct}% of your cash (roster move #${f.moveNo} this week).`;
}
